import { BaseQueryFn } from '@reduxjs/toolkit/dist/query'
import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import {
  BASE_URL,
  BOB_CUSTOMER_URL,
  BOB_FOUNDATION_URL,
  BOB_LMS_URL,
  BOB_MASTER_URL,
  BOB_PARTNER_URL,
  BOB_PRODUCT_URL,
  FOUNDATION_URL,
  MISC_URL,
  BOB_OPERATION_URL,
  LEADS_URL,
  EQUITAS_URL,
  HEALTH_URL,
  BOB_FMS_BASE_URL,
  COMMUNICATION_URL,
  BOB_FMS_BASE_URL_REAL,
  BOB_BGR_URL,
  BOB_TRAVEL_URL,
  GROUP_URL,
  iv,
  aesKey,
} from 'Constants'
import { updateClientIp, resetUser } from 'features/Authentication/user.slice'
import { IApiResponse } from 'Models/ResponseModels'
import { store } from '../app/store'
import { detectDeviceType } from './general'
import { ToastService } from './toast.service'
import pjs from '../../package.json'
import { decryptMessage, encryptMessage } from 'Utils'
import {
  decryptSecure,
  encryptSecure,
  getSecureEnvelope,
  getSessionKeyHeader,
} from './secureTransport'
import { WANTED_URLS } from './encryptedEndpoints'

/**
 * Server errors arrive either as a plain string or, for field validation, as
 * { field: ["msg"] }. Passing the object straight to a toast hands React an object
 * child and crashes the page, so flatten it to readable text first.
 */
const toToastMessage = (message: unknown): string => {
  if (typeof message === 'string') {
    return message
  }
  if (message && typeof message === 'object') {
    const parts = Object.values(message as Record<string, unknown>)
      .flat()
      .filter((part): part is string => typeof part === 'string')
    if (parts.length) {
      return parts.join(' ')
    }
  }
  return ''
}

export const getDefaultHeaders = ({ token }: { token: string }) => {
  const tempHeaders: any = { 'Content-Type': 'application/json' }
  // const headersExtendedUrls = [BOB_CUSTOMER_URL, BOB_PRODUCT_URL, BOB_MASTER_URL] Lang: 'EN'
  // if (
  //   baseUrl
  //   // &&
  //   // headersExtendedUrls.includes(baseUrl)
  // ) {
  const lead_id = store.getState().planData ? store.getState().planData.lead_id : ''
  const acc_number =
    store.getState().cbsData &&
    store.getState().cbsData.customerDetails &&
    store.getState().cbsData.customerDetails &&
    store.getState().cbsData.customerDetails?.account_details &&
    store.getState().cbsData.customerDetails?.account_details[0]
      ? store.getState().cbsData.customerDetails?.account_details[0].acc_number
      : ''
  const application_number =
    store.getState().planData &&
    store.getState().planData.planQuoteResponse &&
    store.getState().planData.planQuoteResponse?.premium_data
      ? store.getState().planData.planQuoteResponse?.premium_data.application_number
      : ''
  const partner_emp_id =
    store.getState().user &&
    store.getState().user.authenticatedUser &&
    store.getState().user.authenticatedUser.sp_user_data
      ? store.getState().user.authenticatedUser.sp_user_data?.partner_emp_id
      : ''
  const uniqueSessionId = store.getState().user && store.getState().user.uniqueSessionId
  const client_ip = store.getState().user && store.getState().user.clientIp
  const isTabit = store.getState().homePage.ptr ? true : false
  const isMobile = detectDeviceType() === 'Mobile' ? true : false
  const isPartial = store.getState().proposalStep.isQuickBuy
  const selectedJourney = store.getState().homePage.journeyType
  let deviceType: 'Mac' | 'Windows' | 'Android' | 'iOS' | 'Others' = 'Others'

  if (window?.navigator) {
    if (window.navigator.userAgent.includes('Macintosh')) {
      deviceType = 'Mac'
    } else if (window.navigator.userAgent.includes('Windows')) {
      deviceType = 'Windows'
    } else if (window.navigator.userAgent.includes('Android')) {
      deviceType = 'Android'
    } else if (window.navigator.userAgent.includes('iPhone')) {
      deviceType = 'iOS'
    }
  }

  if (lead_id) {
    tempHeaders['Lead-id'] = lead_id
  }
  // if (acc_number) {
  //   tempHeaders['Account-no'] = acc_number
  // }
  if (application_number) {
    tempHeaders['Application-id'] = application_number
  }
  if (partner_emp_id) {
    tempHeaders['Partner-employee-id'] = partner_emp_id
  }
  // if (client_ip) {
  //   // tempHeaders['X-Forwarded-For'] = client_ip
  //   // tempHeaders['Remote-Address'] = client_ip
  // }
  if (uniqueSessionId) {
    tempHeaders['X-Forwarded-For'] = uniqueSessionId
  }

  if (isTabit) {
    tempHeaders['Source'] = 'TABIT'
  } else {
    tempHeaders['Source'] = 'Smartinsure'
  }

  if (selectedJourney?.toLowerCase() === 'life') {
    if (window.location.pathname !== '/life/proposal') {
      tempHeaders['Journey-Type'] = 'Default'
    } else {
      tempHeaders['Journey-Type'] = isPartial ? 'Partial' : 'E2E'
    }
  }

  if (deviceType) {
    tempHeaders['Device'] = deviceType
  }
  if (pjs?.version) {
    tempHeaders['App-Version'] = `v${pjs?.version}`
  }
  // }
  if (token) {
    tempHeaders['Authorization'] = `Token ${token}`
    return tempHeaders
  }
  return tempHeaders
}

const formDataToObject = (formData: FormData) => {
  const obj: Record<string, any> = {}
  for (const [key, value] of formData.entries() as any) {
    // If it's a File, optionally handle differently
    obj[key] = value instanceof File ? value.name : value
  }
  return obj
}

export const getFileHeaders = (token: string) => {
  const tempHeaders = { 'Content-Type': 'multipart/form-data' }
  if (token) {
    return {
      ...tempHeaders,
      Authorization: `Token ${token}`,
    }
  }
  return tempHeaders
}

export const axiosBaseQuery =
  ({
    baseUrl,
  }: {
    baseUrl: string
  }): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      body?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
      responseType?: AxiosRequestConfig['responseType']
      rawResponse?: boolean
    },
    unknown,
    unknown
  > =>
  async ({ url, method, body, params, headers, responseType, rawResponse = false }, { signal }) => {
    const token = store.getState().user.authenticatedUser.authtoken
    const { journeyType } = store.getState().homePage
    const wantedURLs = WANTED_URLS

    // Allowlist + PROD guard live in secureTransport.ts (single source of truth).
    const secureEnvelope = getSecureEnvelope(url)
    const isSecure = !!secureEnvelope

    // 'request_data' endpoints are encrypted at the call site (their payloads are built
    // by hand), so here we only attach the header and leave body/response alone.
    const isSecureEnvelopeHandledHere = secureEnvelope === 'encrypted_data'

    // const iv = process.env.REACT_APP_STATIC_IV ?? ''
    // const aesKey = process.env.REACT_APP_STATIC_AES_KEY ?? ''

    console.log(`[api ▶] ${url}`, {
      body: body instanceof FormData ? '(FormData)' : body,
      params,
    })

    /* ---------- DISABLED (VAPT #2): legacy static-key envelope ----------
    const encryptData = async (data: any) => ({
      encrypted_data: await encryptMessage(JSON.stringify(data), aesKey, iv).then(
        (resp) => resp.encryptedData,
      ),
    })
    --------------------------------------------------------------------- */

    const encryptSecureData = async (data: any) =>
      secureEnvelope === 'request_data'
        ? { request_data: await encryptSecure(data) }
        : { encrypted_data: await encryptSecure(data) }

    const encryptedBody = isSecureEnvelopeHandledHere && body ? await encryptSecureData(body) : body
    // const encryptData = async (data: any) => {
    //   const plainData = data instanceof FormData ? formDataToObject(data) : data
    //   return {
    //     encrypted_data: await encryptMessage(JSON.stringify(plainData), aesKey, iv).then(
    //       (resp) => resp.encryptedData,
    //     ),
    //   }
    // }

    // const encryptedBody = wantedURLs.includes(url) && body ? await encryptData(body) : body
    const encryptedParams =
      isSecureEnvelopeHandledHere && params ? await encryptSecureData(params) : params

    // Sent ONLY for migrated endpoints — it is the server's scheme discriminator.
    const sessionKeyHeader = isSecure ? { 'x-session-key': await getSessionKeyHeader() } : {}

    const compositeHeaders = {
      ...getDefaultHeaders({
        token: token ?? '',
      }),
      ...headers,
    }

    if (body instanceof FormData) {
      delete compositeHeaders['Content-Type']
    }

    try {
      const result = await axios({
        url: baseUrl + url,
        method,
        // data: body,
        // params,
        data: encryptedBody,
        params: encryptedParams,
        headers: {
          ...getDefaultHeaders({
            token: token ?? '',
          }),
          ...sessionKeyHeader,
          ...(headers ?? {}),
        },

        responseType,
        signal,
      })
      if (result.headers['x-client-ip']) {
        store.dispatch(updateClientIp(result.headers['x-client-ip']))
      }
      if (responseType === 'text' || responseType === 'arraybuffer') {
        return result
      }
      // Decrypt response for wanted URLs
      let responseData = result.data
      if (isSecureEnvelopeHandledHere) {
        // §8: crypto failures come back as PLAINTEXT json (no encrypted field) with 400,
        // so an absent blob is not an error here — fall through with the plain body.
        const secureBlob = responseData?.encrypted_data

        if (secureBlob) {
          responseData = await decryptSecure(secureBlob)
        } else {
          console.warn(
            `[api ◀] ${url} — response was NOT encrypted.`,
            'Header stripped by proxy/WAF, path not allowlisted server-side, or a crypto error.',
          )
        }
      }
      /* ---------- DISABLED (VAPT #2): legacy static-key response decrypt ----------
      else if (wantedURLs.includes(url) && responseData?.encrypted_data) {
        responseData = await decryptMessage(responseData.encrypted_data, aesKey, iv).then(
          (resp) => resp.decryptedData,
        )
      }
      ---------------------------------------------------------------------------- */

      console.log(`[api ◀] ${url}`, responseData)

      // console.log(
      //   url,
      //   params,
      //   '===============================params===============================',
      // )
      // console.log(
      //   url,
      //   body?.data
      //     ? await decryptMessage(body.data, aesKey, iv).then((resp) => resp.decryptedData)
      //     : body,
      //   '===============================body===============================',
      // )
      // console.log(
      //   url,
      //   responseData?.data?.encrypted_data
      //     ? await decryptMessage(responseData.data.encrypted_data, aesKey, iv).then(
      //         (resp) => resp.decryptedData,
      //       )
      //     : responseData,
      //   '===============================responseData===============================',
      // )
      if (responseData?.status?.toString() === '401') {
        store.dispatch(resetUser())
        ToastService.showErrorToast({
          title: 'Session timed out, please login again',
        })
        return {
          data: responseData,
        }
      }

      if (responseData?.status?.toString()[0] !== '2' && responseData?.success !== true) {
        // pincode search: the reason is in data.error_text ("No Data Found!"), message is only "Failure"
        const toastMessage =
          (url?.includes('pincode_list') && toToastMessage(responseData?.data?.error_text)) ||
          toToastMessage(responseData.message)
        if (toastMessage) {
          ToastService.showErrorToast({
            title: toastMessage,
          })
        }
        throw responseData
      }
      if (rawResponse) {
        return { data: responseData }
      }
      return { data: responseData.data }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const err = error as AxiosError
        // console.log({ axiosError: err.response?.status })

        if (err.response?.status?.toString() === '401') {
          store.dispatch(resetUser())
          return {
            error: {
              status: err.status,
              message: 'Session timed out, please login again',
            },
          }
        }

        const responseData: any = err && err.response && err.response.data && err.response.data
        // HTTP 429 with no server message (e.g. DRF throttle on the staff-discount check,
        // VAPT A-42 - body is only { detail }): show a friendly message instead of
        // "status code 429". A 429 that carries its own message (login lockout) keeps it.
        const toastMessage =
          (url?.includes('pincode_list') && toToastMessage(responseData?.data?.error_text)) ||
          toToastMessage(responseData?.message) ||
          (err.response?.status === 429
            ? 'Too many attempts, please try again in a few minutes.'
            : '')
        ToastService.showErrorToast({
          title: toastMessage || err.message,
        })
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        }
      } else {
        const err = error as IApiResponse
        return {
          error: {
            status: err.status,
            data: err.data,
            message: err.message,
          },
        }
      }
    }
  }

const baseUrlQuery = axiosBaseQuery({
  baseUrl: BASE_URL,
})

const foundationUrlQuery = axiosBaseQuery({
  baseUrl: FOUNDATION_URL,
})

const bobFoundationUrlQuery = axiosBaseQuery({
  baseUrl: BOB_FOUNDATION_URL,
})

const miscUrlQueryWrapper = axiosBaseQuery({
  baseUrl: MISC_URL,
})

const bobCustomerUrlQuery = axiosBaseQuery({
  baseUrl: BOB_CUSTOMER_URL,
})

const bobProductUrlQuery = axiosBaseQuery({
  baseUrl: BOB_PRODUCT_URL,
})

const bobPartnerUrlQuery = axiosBaseQuery({
  baseUrl: BOB_PARTNER_URL,
})
const bobMasterUrlQuery = axiosBaseQuery({
  baseUrl: BOB_MASTER_URL,
})
const bobLmsUrlQuery = axiosBaseQuery({
  baseUrl: BOB_LMS_URL,
})
const bobOperationUrlQuery = axiosBaseQuery({
  baseUrl: BOB_OPERATION_URL,
})

const bobFmsUrlQuery = axiosBaseQuery({
  baseUrl: BOB_FMS_BASE_URL,
})

const leadsUrlQuery = axiosBaseQuery({
  baseUrl: LEADS_URL,
})
const equitasUrlQuery = axiosBaseQuery({
  baseUrl: EQUITAS_URL,
})

const communicationUrlQuery = axiosBaseQuery({
  baseUrl: COMMUNICATION_URL,
})
const healthUrlQuery = axiosBaseQuery({
  baseUrl: HEALTH_URL,
})
const bgrUrlQuery = axiosBaseQuery({
  baseUrl: BOB_BGR_URL,
})

const bobFmsRealUrlQuery = axiosBaseQuery({
  baseUrl: BOB_FMS_BASE_URL_REAL,
})

const groupQuery = axiosBaseQuery({
  baseUrl: GROUP_URL,
})

const bobTravelUrlQuery = axiosBaseQuery({
  baseUrl: BOB_TRAVEL_URL,
})

export {
  baseUrlQuery,
  foundationUrlQuery,
  miscUrlQueryWrapper,
  bobCustomerUrlQuery,
  bobProductUrlQuery,
  bobPartnerUrlQuery,
  bobFoundationUrlQuery,
  bobMasterUrlQuery,
  bobLmsUrlQuery,
  bobOperationUrlQuery,
  bobFmsUrlQuery,
  leadsUrlQuery,
  equitasUrlQuery,
  healthUrlQuery,
  communicationUrlQuery,
  bobFmsRealUrlQuery,
  bgrUrlQuery,
  groupQuery,
  bobTravelUrlQuery,
}
